import { reactive, toRefs } from "vue";
import { useApiWithAuth } from "@/modules/api";
import { useAuth } from "@/modules/auth";
import { clearObject } from "@/modules/utils";
import {
  DocumentoRntrc,
  RntrcSolicitacaoFilterPayload,
  RntrcSolicitacaoPayload,
  RntrcSolicitacaoVeiculoPayload,
  RntrcTipoServicoPayload,
  TipoServicoRntrc
} from "./rntrcTypes";

const useRntrcSolicitacaoService = () => {
  const {
    data,
    loading,
    get,
    post,
    put,
    downloadFile,
    downloadFileNewTab
  } = useApiWithAuth();

  const listar = (
    filter: RntrcSolicitacaoFilterPayload,
    page: number,
    silencioso?: boolean
  ): Promise<void> => {
    const { situacoes, ...resto } = filter;
    return get(
      "/rntrc/solicitacao",
      {
        ...clearObject({ ...resto }),
        situacoes: situacoes && situacoes.length ? situacoes.join(",") : null,
        page: page
      },
      silencioso ? ({ skipEmptyToast: true } as Record<string, unknown>) : {}
    );
  };

  const buscar = (idRntrcSolicitacao: number): Promise<void> => {
    return get(`/rntrc/solicitacao/${idRntrcSolicitacao}`);
  };

  const criar = (entity: RntrcSolicitacaoPayload): Promise<void> => {
    return post("/rntrc/solicitacao", entity);
  };

  const atualizar = (
    idRntrcSolicitacao: number,
    entity: RntrcSolicitacaoPayload
  ): Promise<void> => {
    return put(`/rntrc/solicitacao/${idRntrcSolicitacao}`, entity);
  };

  const reenviar = (
    idRntrcSolicitacao: number,
    flProsseguirParcial?: boolean
  ): Promise<void> => {
    return post(`/rntrc/solicitacao/${idRntrcSolicitacao}/reenviar`, {
      flProsseguirParcial: !!flProsseguirParcial
    });
  };

  const confirmarPagamento = (idRntrcSolicitacao: number): Promise<void> => {
    return post(`/rntrc/solicitacao/${idRntrcSolicitacao}/confirmar-pagamento`);
  };

  const cancelar = (idRntrcSolicitacao: number): Promise<void> => {
    return post(`/rntrc/solicitacao/${idRntrcSolicitacao}/cancelar`);
  };

  /**
   * "Prosseguir com a exclusão": a placa está presa num contrato de outro transportador; a
   * GovPass exclui de lá e, paga a exclusão, segue com a inclusão sozinha.
   */
  const excluirFrotaAtual = (idRntrcSolicitacao: number): Promise<void> => {
    return post(`/rntrc/solicitacao/${idRntrcSolicitacao}/excluir-frota-atual`);
  };

  /**
   * Cancelamento na ANTT antes de pagar: sem placas (ou com todas) o pedido inteiro; com
   * algumas, só elas saem e o robô gera um PIX novo com o que sobrou.
   */
  const cancelarNaAntt = (
    idRntrcSolicitacao: number,
    idsVeiculos?: Array<number>
  ): Promise<void> => {
    return post(`/rntrc/solicitacao/${idRntrcSolicitacao}/cancelar-antt`, {
      idsVeiculos: idsVeiculos ?? []
    });
  };

  /** Link temporário do documento (texto puro), para usar como src de imagem. */
  const linkDocumento = (
    idRntrcSolicitacao: number,
    tipo: DocumentoRntrc
  ): Promise<void> => {
    return get(`/rntrc/solicitacao/${idRntrcSolicitacao}/documento/${tipo}`);
  };

  /** Abre o documento pelo link temporário, no mesmo padrão do download do CRLV. */
  const abrirDocumento = (
    idRntrcSolicitacao: number,
    tipo: DocumentoRntrc,
    newTab?: boolean
  ): Promise<void> => {
    return (newTab ? downloadFileNewTab : downloadFile)(
      `/rntrc/solicitacao/${idRntrcSolicitacao}/documento/${tipo}`
    );
  };

  /** Consulta de veículo: extrato ou certificado do transportador atual de uma placa. */
  const abrirDocumentoVeiculo = (
    idRntrcSolicitacao: number,
    idRntrcSolicitacaoVeiculo: number,
    tipo: "EXT" | "CER",
    newTab?: boolean
  ): Promise<void> => {
    return (newTab ? downloadFileNewTab : downloadFile)(
      `/rntrc/solicitacao/${idRntrcSolicitacao}/veiculo/${idRntrcSolicitacaoVeiculo}/documento/${tipo}`
    );
  };

  /**
   * "Baixar todos": o .zip com os PDFs (bytes em data), para gravar onde o usuário escolher no
   * "Salvar como".
   */
  const buscarZipDocumentos = (idRntrcSolicitacao: number): Promise<void> => {
    return get(
      `/rntrc/solicitacao/${idRntrcSolicitacao}/documentos/zip`,
      undefined,
      {
        responseType: "arraybuffer"
      }
    );
  };

  /** Navegador sem "Salvar como" (Firefox, Safari): o .zip como um download comum. */
  const abrirZipDocumentos = (idRntrcSolicitacao: number): Promise<void> => {
    return downloadFile(
      `/rntrc/solicitacao/${idRntrcSolicitacao}/documentos/zip`
    );
  };

  /** Root pode informar o cliente (preços daquele cliente). */
  const tiposHabilitados = (idCliente?: number | null): Promise<void> => {
    return get("/rntrc/solicitacao/tipos", { idCliente });
  };

  const safeFilter = (): RntrcSolicitacaoFilterPayload => {
    return { situacoes: [] };
  };

  return {
    data,
    loading,
    listar,
    buscar,
    criar,
    atualizar,
    reenviar,
    confirmarPagamento,
    cancelar,
    cancelarNaAntt,
    excluirFrotaAtual,
    linkDocumento,
    abrirDocumento,
    abrirDocumentoVeiculo,
    buscarZipDocumentos,
    abrirZipDocumentos,
    tiposHabilitados,
    safeFilter
  };
};

// ---------------------------------------------------------------------------
// Tipos habilitados: carregados uma vez por sessão (por cliente logado) e reusados pelo
// menu e pelo formulário.
// ---------------------------------------------------------------------------

interface RntrcTiposState {
  tipos: Array<RntrcTipoServicoPayload>;
  tiposLoaded: boolean;
  idClienteTipos: number | null;
}

const tiposState = reactive<RntrcTiposState>({
  tipos: [],
  tiposLoaded: false,
  idClienteTipos: null
});

let tiposPromise: Promise<Array<RntrcTipoServicoPayload>> | null = null;

const useRntrcTipos = () => {
  const loadTipos = (force?: boolean) => {
    const idCliente = useAuth().user?.value?.idCliente ?? null;
    if (
      !force &&
      tiposState.tiposLoaded &&
      tiposState.idClienteTipos === idCliente
    ) {
      return Promise.resolve(tiposState.tipos);
    }
    if (!force && tiposPromise && tiposState.idClienteTipos === idCliente) {
      return tiposPromise;
    }
    tiposState.idClienteTipos = idCliente;
    const { data, tiposHabilitados } = useRntrcSolicitacaoService();
    tiposPromise = tiposHabilitados()
      .then(() => {
        tiposState.tipos = Array.isArray(data.value) ? data.value : [];
        tiposState.tiposLoaded = true;
        return tiposState.tipos;
      })
      .catch(() => {
        tiposPromise = null;
        return [] as Array<RntrcTipoServicoPayload>;
      });
    return tiposPromise;
  };

  const getTipo = (flag?: TipoServicoRntrc | null) =>
    tiposState.tipos.find((t) => t.flTipoServico === flag);

  /** Root escolhendo o cliente na tela: tipos e preços daquele cliente, sem cache. */
  const loadTiposCliente = (idCliente: number) => {
    const { data, tiposHabilitados } = useRntrcSolicitacaoService();
    return tiposHabilitados(idCliente)
      .then(
        () =>
          (Array.isArray(data.value) ? data.value : []) as Array<
            RntrcTipoServicoPayload
          >
      )
      .catch(() => [] as Array<RntrcTipoServicoPayload>);
  };

  return {
    loadTipos,
    loadTiposCliente,
    getTipo,
    ...toRefs(tiposState)
  };
};

// ---------------------------------------------------------------------------
// Ações por solicitação. O permissionInfo da solicitação manda (authorities view, edit,
// replay, cancel_play e download, bloqueadas conforme a situação); sem ele, a regra da
// situação com as permissões gerais do módulo.
// ---------------------------------------------------------------------------

type AcaoRntrc =
  | "editar"
  | "reenviar"
  | "cancelar"
  | "download"
  | "confirmar-pagamento"
  | "cancelar-pedido-antt"
  | "cancelar-placas-antt"
  | "excluir-frota-atual"
  | "incluir-destino"
  | "fazer-cadastro"
  | "outro-servico";

/** Pedidos de movimentação de frota: o robô sabe cancelar no SITCarga (13/10/2026). */
const TIPOS_CANCELAVEIS_NA_ANTT: Array<TipoServicoRntrc> = [
  "IMF",
  "IAR",
  "EXC"
];

/** Placas que seguem no pedido: entraram (ou ainda não foram processadas) e não saíram. */
const placasAtivas = (
  s?: RntrcSolicitacaoPayload | null
): Array<RntrcSolicitacaoVeiculoPayload> =>
  (s?.veiculos ?? []).filter(
    (v) => v.flIncluido !== false && !v.dataCancelamento
  );

/** Antes de pagar o PIX, sem outro cancelamento em andamento. */
const cancelavelNaAntt = (s: RntrcSolicitacaoPayload) =>
  s.flSituacao === "PAG" &&
  !s.flAcaoRobo &&
  !!s.flTipoServico &&
  TIPOS_CANCELAVEIS_NA_ANTT.indexOf(s.flTipoServico) >= 0;

const podeNaSolicitacao = (
  acao: AcaoRntrc,
  s?: RntrcSolicitacaoPayload | null
): boolean => {
  if (!s) {
    return false;
  }
  const auth = useAuth();
  const geral = (authority: string) => !!auth.amIAllowed(authority, "rntrc");
  const daSolicitacao = (authority: string, fallback: boolean) =>
    s.permissionInfo
      ? !!auth.amIAllowed(authority, s.permissionInfo)
      : fallback;
  switch (acao) {
    case "editar":
      return daSolicitacao(
        "edit",
        (s.flSituacao === "AGR" || s.flSituacao === "ERR") &&
          !s.idPedidoSitcarga &&
          geral("edit")
      );
    case "reenviar":
      return daSolicitacao("replay", s.flSituacao === "ERR" && geral("edit"));
    case "cancelar":
      return daSolicitacao(
        "cancel_play",
        s.flSituacao === "ERR" && geral("edit")
      );
    case "download":
      return daSolicitacao("download", geral("download"));
    case "confirmar-pagamento":
      // não há authority própria: situação PAG + edição no módulo. Na exclusão da frota atual o
      // PIX é o da exclusão, que só o detalhe traz.
      return (
        (s.flSituacao === "PAG" ||
          (s.flSituacao === "AEX" &&
            (s.exclusoes ?? []).some((e) => e.flSituacao === "PAG"))) &&
        geral("edit")
      );
    case "excluir-frota-atual":
      return s.flSituacao === "EXN" && geral("edit");
    case "cancelar-pedido-antt":
      return cancelavelNaAntt(s) && geral("edit");
    case "cancelar-placas-antt":
      // só faz sentido com mais de uma placa no pedido; a lista não traz os veículos
      return (
        cancelavelNaAntt(s) &&
        geral("edit") &&
        (s.veiculos ? placasAtivas(s).length : s.quantidadeVeiculos ?? 0) > 1
      );
    case "incluir-destino":
      return (
        s.flTipoServico === "EXC" && s.flSituacao === "CON" && geral("create")
      );
    case "fazer-cadastro":
      // a consulta disse "sem cadastro": o próximo passo é cadastrar na ANTT
      return (
        s.flTipoServico === "CTR" && s.flSituacao === "TSC" && geral("create")
      );
    case "outro-servico":
      // consulta de veículo terminada: as placas seguem para outro serviço, já preenchidas
      return (
        s.flTipoServico === "CVE" &&
        (s.flSituacao === "CON" ||
          s.flSituacao === "PSC" ||
          s.flSituacao === "TSC") &&
        geral("create")
      );
  }
  return false;
};

/**
 * O PIX que o cliente tem a pagar agora: o da própria solicitação ou, na exclusão da frota atual,
 * o da exclusão em aberto.
 */
const pixEmAberto = (
  s?: RntrcSolicitacaoPayload | null
): RntrcSolicitacaoPayload | null => {
  if (!s) {
    return null;
  }
  if (s.flSituacao === "PAG" || s.flSituacao === "PGI") {
    return s;
  }
  if (s.flSituacao === "AEX") {
    return (
      (s.exclusoes ?? []).find(
        (e) =>
          (e.flSituacao === "PAG" || e.flSituacao === "PGI") && !!e.codigoPix
      ) ?? null
    );
  }
  return null;
};

// ---------------------------------------------------------------------------
// Consulta de veículo → outro serviço com as placas consultadas
// ---------------------------------------------------------------------------

/** Os serviços que a consulta de veículo oferece com as placas dela. */
const SERVICOS_APOS_CONSULTA: Array<TipoServicoRntrc> = [
  "IMF",
  "IAR",
  "EXC",
  "TRF",
  "ALT"
];

/**
 * Exclusão, transferência e alteração saem no nome de quem tem a placa hoje: só servem placas
 * que estão numa frota, e de um transportador por solicitação.
 */
const servicoNoNomeDoDonoAtual = (tipo?: TipoServicoRntrc | null) =>
  tipo === "EXC" || tipo === "TRF" || tipo === "ALT";

/** A consulta achou a placa na frota de alguém. */
const placaEmFrota = (v: RntrcSolicitacaoVeiculoPayload) =>
  (v.cpfCnpjAtual ?? "").replace(/\D/g, "").length >= 11;

/** As placas da consulta que servem ao serviço (inclusão: todas). */
const placasParaServico = (
  s: RntrcSolicitacaoPayload | null | undefined,
  tipo?: TipoServicoRntrc | null
): Array<RntrcSolicitacaoVeiculoPayload> =>
  (s?.veiculos ?? []).filter(
    (v) => !servicoNoNomeDoDonoAtual(tipo) || placaEmFrota(v)
  );

/** CPF (11 dígitos) é TAC; CNPJ (14) é ETC. */
const tipoNovoCadastro = (cpfCnpj?: string | null): TipoServicoRntrc =>
  (cpfCnpj ?? "").replace(/\D/g, "").length === 11 ? "CPF" : "CPJ";

export {
  useRntrcSolicitacaoService,
  useRntrcTipos,
  podeNaSolicitacao,
  placasAtivas,
  pixEmAberto,
  tipoNovoCadastro,
  SERVICOS_APOS_CONSULTA,
  servicoNoNomeDoDonoAtual,
  placaEmFrota,
  placasParaServico
};
